import React, { useEffect, useState } from 'react';
import {
    Table,
    Button,
    Space,
    Tag,
    Modal,
    Descriptions,
    Select,
    message,
    Input,
    Tooltip,
    Card,
    Row,
    Col,
} from 'antd';
import {
    SearchOutlined,
    CheckOutlined,
    CloseOutlined,
    EyeOutlined,
} from '@ant-design/icons';
import { apiCall } from '../../API/RestApi';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

const { Option } = Select;

const AssetDelegationRequest = () => {
    const token = useSelector((state) => state.user.token);
    const user = useSelector((state) => state.user);

    const [data, setData] = useState([]);
    const [meta, setMeta] = useState({ total: 0, page: 1, limit: 10 });
    const [loading, setLoading] = useState(false);

    const [selectedAssetRequest, setSelectedAssetRequest] = useState(null);
    const [detailVisible, setDetailVisible] = useState(false);

    const [statusFilter, setStatusFilter] = useState('');
    const [delegationStatus, setDelegationStatus] = useState('');
    const [reason, setReason] = useState('');
    const [editModalVisible, setEditModalVisible] = useState(false);

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [bulkModalVisible, setBulkModalVisible] = useState(false);
    const [bulkStatus, setBulkStatus] = useState('');
    const [bulkReason, setBulkReason] = useState('');

    const [search, setSearch] = useState('');

    const getHeaders = () => ({
        Authorization: 'Bearer ' + token,
    });

    const getLoginUserId = () => {
        try {
            return JSON.parse(user.user)?.users_id;
        } catch {
            return null;
        }
    };

    const dateFormat = (value) => {
        if (!value) return '-';
        return dayjs(value).format('YYYY-MM-DD');
    };

    const renderDelegationTag = (status) => {
        if (status === 'Approved') return <Tag color="green">Approved</Tag>;
        if (status === 'Pending') return <Tag color="orange">Pending</Tag>;
        if (status === 'Rejected') return <Tag color="red">Rejected</Tag>;
        if (status === 'Skipped') return <Tag color="blue">Skipped</Tag>;
        return <Tag>{status || '-'}</Tag>;
    };

    const renderApprovalTag = (status) => {
        if (status === 'Waiting Authorization') return <Tag color="purple">Waiting Authorization</Tag>;
        if (status === 'Pending') return <Tag color="orange">Pending Final</Tag>;
        if (status === 'Approved') return <Tag color="green">Approved</Tag>;
        if (status === 'Rejected') return <Tag color="red">Rejected</Tag>;
        return <Tag>{status || '-'}</Tag>;
    };

    const renderStatusTag = (status) => {
        if (status === 'assigned') return <Tag color="blue">Assigned</Tag>;
        if (status === 'pending') return <Tag color="orange">Pending</Tag>;
        if (status === 'returned') return <Tag color="green">Returned</Tag>;
        if (status === 'rejected') return <Tag color="red">Rejected</Tag>;
        return <Tag>{status || '-'}</Tag>;
    };

    const getDelegationStatus = (record) =>
        record?.delegation_status || record?.approval?.delegation_status || '-';

    const getApprovalStatus = (record) =>
        record?.approval_status || record?.approval?.status || '-';

    const getRequester = (record) =>
        record?.user?.name || record?.approval?.user?.name || '-';

    const getCategoryName = (record) =>
        record?.category?.name ||
        record?.asset?.category?.name ||
        record?.asset?.category?.asset_category_name ||
        '-';

    const fetchData = async (page = 1, limit = 10) => {
        setLoading(true);

        try {
            const params = new URLSearchParams({
                page,
                limit,
                search: search || '',
                delegation_status: statusFilter || '',
                delegation_users_id: getLoginUserId() || '',
            });

            const res = await apiCall({
                method: 'GET',
                url: `/asset-assignments?${params.toString()}`,
                data: {
                    headers: getHeaders(),
                },
            });

            if (res.status === 'success') {
                setData(res.data.data || []);
                setMeta({
                    page: res.data.page || 1,
                    limit: res.data.limit || 10,
                    total: res.data.total_data || 0,
                });
            } else {
                message.error(res.message || 'Failed to fetch data');
            }
        } catch (error) {
            message.error('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            fetchData(1, meta.limit);
        }, 500);

        return () => clearTimeout(timeout);
    }, [search, statusFilter]);

    const openActionModal = (record, status) => {
        setSelectedAssetRequest(record);
        setDelegationStatus(status);
        setReason('');
        setEditModalVisible(true);
    };

    const handleChangeStatus = async () => {
        if (!delegationStatus) {
            return message.warning('Please select a status');
        }

        try {
            const res = await apiCall({
                method: 'PUT',
                url: `/asset-assignments/${selectedAssetRequest.asset_assignment_id}/delegation`,
                data: {
                    data: {
                        delegation_status: delegationStatus,
                        delegation_notes: reason || '',
                    },
                    headers: {
                        ...getHeaders(),
                        'Content-Type': 'application/json',
                    },
                },
            });

            if (res.status === 'success') {
                message.success(res.message || 'Authorization updated successfully');
                setEditModalVisible(false);
                setDetailVisible(false);
                setReason('');
                setDelegationStatus('');
                fetchData(meta.page, meta.limit);
            } else {
                message.error(res.message || 'Failed to update delegation');
            }
        } catch (error) {
            message.error(error?.response?.data?.message || 'Failed to update delegation');
        }
    };

    const openBulkModal = (status) => {
        setBulkStatus(status);
        setBulkReason('');
        setBulkModalVisible(true);
    };

    const handleBulkDelegation = async () => {
        if (selectedRowKeys.length === 0) {
            return message.warning('Please select at least one record');
        }

        Modal.confirm({
            title: `${bulkStatus === 'Approved' ? 'Approve' : 'Reject'} ${selectedRowKeys.length} delegation request?`,
            content: 'Data yang diproses hanya asset assignment dengan delegation status Pending.',
            okText: bulkStatus === 'Approved' ? 'Approve' : 'Reject',
            okType: bulkStatus === 'Rejected' ? 'danger' : 'primary',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    setLoading(true);

                    const res = await apiCall({
                        method: 'PUT',
                        url: `/asset-assignments/bulk-delegation`,
                        data: {
                            data: {
                                ids: selectedRowKeys.map(Number),
                                delegation_status: bulkStatus,
                                delegation_notes: bulkReason || '',
                            },
                            headers: {
                                ...getHeaders(),
                                'Content-Type': 'application/json',
                            },
                        },
                    });

                    if (res.status === 'success') {
                        message.success(res.message || 'Bulk delegation berhasil diproses');
                        setBulkModalVisible(false);
                        setSelectedRowKeys([]);
                        setBulkStatus('');
                        setBulkReason('');
                        fetchData(meta.page, meta.limit);
                    } else {
                        message.error(res.message || 'Failed to update bulk delegation');
                    }
                } catch (error) {
                    message.error(error?.response?.data?.message || 'Failed to update bulk delegation');
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const columns = [
        {
            title: 'No',
            width: 70,
            render: (_, __, index) => (meta.page - 1) * meta.limit + index + 1,
        },
        {
            title: 'Requester',
            width: 180,
            render: (_, record) => getRequester(record),
        },
        {
            title: 'Asset',
            width: 240,
            render: (_, record) =>
                record.asset
                    ? `${record.asset.asset_name || '-'}${record.asset.asset_code ? ` - ${record.asset.asset_code}` : ''}`
                    : '-',
        },
        {
            title: 'Category',
            width: 150,
            render: (_, record) => getCategoryName(record),
        },
        {
            title: 'Request Date',
            width: 130,
            render: (_, record) => dateFormat(record.request_date),
        },
        {
            title: 'Status',
            width: 120,
            render: (_, record) => renderStatusTag(record.status),
        },
        {
            title: 'Authorization',
            width: 140,
            render: (_, record) => renderDelegationTag(getDelegationStatus(record)),
        },
        {
            title: 'Approval',
            width: 160,
            render: (_, record) => renderApprovalTag(getApprovalStatus(record)),
        },
        {
            title: 'Actions',
            fixed: 'right',
            width: 190,
            render: (_, record) => (
                <Space>
                    <Tooltip title="Detail">
                        <Button
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => {
                                setSelectedAssetRequest(record);
                                setDetailVisible(true);
                            }}
                        />
                    </Tooltip>

                    <Tooltip title="Approve Authorization">
                        <Button
                            size="small"
                            type="primary"
                            icon={<CheckOutlined />}
                            disabled={getDelegationStatus(record) !== 'Pending'}
                            onClick={() => openActionModal(record, 'Approved')}
                        />
                    </Tooltip>

                    <Tooltip title="Reject Authorization">
                        <Button
                            size="small"
                            danger
                            icon={<CloseOutlined />}
                            disabled={getDelegationStatus(record) !== 'Pending'}
                            onClick={() => openActionModal(record, 'Rejected')}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
        getCheckboxProps: (record) => ({
            disabled: getDelegationStatus(record) !== 'Pending',
        }),
    };

    const ApprovalLine = ({ data }) => {
        const approval = data?.approval;

        const steps = [
            {
                title: 'Requester',
                name: data?.user?.name || approval?.user?.name || '-',
                status: 'Submitted',
                date: data?.created_at,
                notes: data?.notes || approval?.notes,
            },
            {
                title: 'Authorization',
                name: approval?.delegation_user?.name || '-',
                status: approval?.delegation_status || '-',
                date: approval?.delegation_date,
                notes: approval?.delegation_notes,
            },
            {
                title: 'Final Approval',
                name: approval?.approval_user?.name || '-',
                status: approval?.status || '-',
                date: approval?.approval_date,
                notes: approval?.approval_notes,
            },
        ];

        const colorByStatus = (status) => {
            if (status === 'Approved' || status === 'Submitted') return '#52c41a';
            if (status === 'Pending' || status === 'Waiting Authorization') return '#faad14';
            if (status === 'Rejected') return '#ff4d4f';
            if (status === 'Skipped') return '#1677ff';
            return '#d9d9d9';
        };

        return (
            <Card title="Approval Line" size="small" style={{ marginTop: 16 }}>
                <Row gutter={16}>
                    {steps.map((item, index) => (
                        <Col span={8} key={item.title}>
                            <div
                                style={{
                                    border: '1px solid #f0f0f0',
                                    borderRadius: 10,
                                    padding: 14,
                                    minHeight: 150,
                                    position: 'relative',
                                    background: '#fff',
                                }}
                            >
                                {index < steps.length - 1 && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: 32,
                                            right: -16,
                                            width: 16,
                                            height: 2,
                                            background: '#d9d9d9',
                                        }}
                                    />
                                )}

                                <div
                                    style={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: '50%',
                                        background: colorByStatus(item.status),
                                        color: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 700,
                                        marginBottom: 10,
                                    }}
                                >
                                    {index + 1}
                                </div>

                                <div style={{ fontWeight: 700, marginBottom: 4 }}>{item.title}</div>
                                <div style={{ color: '#555', marginBottom: 8 }}>{item.name}</div>

                                <Tag
                                    color={
                                        item.status === 'Approved' || item.status === 'Submitted'
                                            ? 'green'
                                            : item.status === 'Rejected'
                                                ? 'red'
                                                : item.status === 'Skipped'
                                                    ? 'blue'
                                                    : 'orange'
                                    }
                                >
                                    {item.status}
                                </Tag>

                                <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                                    {item.date ? dayjs(item.date).format('YYYY-MM-DD HH:mm') : '-'}
                                </div>

                                {item.notes && (
                                    <div style={{ marginTop: 8, fontSize: 12, color: '#555' }}>
                                        “{item.notes}”
                                    </div>
                                )}
                            </div>
                        </Col>
                    ))}
                </Row>
            </Card>
        );
    };

    return (
        <div>
            <h2>Asset Authorization Request</h2>

            <Card style={{ marginBottom: 16 }}>
                <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space wrap>
                        <Input
                            placeholder="Search asset"
                            value={search}
                            prefix={<SearchOutlined />}
                            onChange={(e) => setSearch(e.target.value)}
                            allowClear
                            style={{ width: 320 }}
                        />

                        <Select
                            placeholder="Filter delegation"
                            allowClear
                            value={statusFilter || undefined}
                            onChange={(value) => setStatusFilter(value || '')}
                            style={{ width: 200 }}
                        >
                            <Option value="Pending">Pending</Option>
                            <Option value="Approved">Approved</Option>
                            <Option value="Rejected">Rejected</Option>
                        </Select>
                    </Space>

                    <Space>
                        <Button
                            type="primary"
                            icon={<CheckOutlined />}
                            disabled={selectedRowKeys.length === 0}
                            onClick={() => openBulkModal('Approved')}
                        >
                            Approve
                        </Button>

                        <Button
                            danger
                            icon={<CloseOutlined />}
                            disabled={selectedRowKeys.length === 0}
                            onClick={() => openBulkModal('Rejected')}
                        >
                            Reject
                        </Button>
                    </Space>
                </Space>
            </Card>

            <Table
                rowSelection={rowSelection}
                columns={columns}
                dataSource={data}
                rowKey="asset_assignment_id"
                loading={loading}
                scroll={{ x: 1300 }}
                pagination={{
                    current: meta.page,
                    pageSize: meta.limit,
                    total: meta.total,
                    showSizeChanger: true,
                }}
                onChange={(pagination) => {
                    setMeta((prev) => ({
                        ...prev,
                        page: pagination.current,
                        limit: pagination.pageSize,
                    }));

                    fetchData(pagination.current, pagination.pageSize);
                }}
            />

            <Modal
                title="Asset Authorization Detail"
                open={detailVisible}
                onCancel={() => setDetailVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setDetailVisible(false)}>
                        Close
                    </Button>,
                ]}
                width={900}
            >
                {selectedAssetRequest && (
                    <Descriptions column={2} bordered size="small">
                        <Descriptions.Item label="Authorization Status">
                            {renderDelegationTag(getDelegationStatus(selectedAssetRequest))}
                        </Descriptions.Item>

                        <Descriptions.Item label="Approval Status">
                            {renderApprovalTag(getApprovalStatus(selectedAssetRequest))}
                        </Descriptions.Item>

                        <Descriptions.Item label="Current Status">
                            {renderStatusTag(selectedAssetRequest.status)}
                        </Descriptions.Item>

                        <Descriptions.Item label="Requester">
                            {getRequester(selectedAssetRequest)}
                        </Descriptions.Item>

                        <Descriptions.Item label="Request Date">
                            {dateFormat(selectedAssetRequest.request_date)}
                        </Descriptions.Item>

                        <Descriptions.Item label="Asset Name">
                            {selectedAssetRequest.asset?.asset_name || '-'}
                        </Descriptions.Item>

                        <Descriptions.Item label="Asset Code">
                            {selectedAssetRequest.asset?.asset_code || '-'}
                        </Descriptions.Item>

                        <Descriptions.Item label="Category">
                            {getCategoryName(selectedAssetRequest)}
                        </Descriptions.Item>

                        <Descriptions.Item label="Notes" span={2}>
                            {selectedAssetRequest.notes || '-'}
                        </Descriptions.Item>

                        <Descriptions.Item label="Authorization Notes" span={2}>
                            {selectedAssetRequest.approval?.delegation_notes || '-'}
                        </Descriptions.Item>
                    </Descriptions>
                )}

                <ApprovalLine data={selectedAssetRequest} />
            </Modal>

            <Modal
                title={`${delegationStatus === 'Approved' ? 'Approve' : 'Reject'} Authorization`}
                open={editModalVisible}
                onCancel={() => setEditModalVisible(false)}
                onOk={handleChangeStatus}
                okText="Submit"
                destroyOnClose
            >
                <Input.TextArea
                    rows={3}
                    placeholder="Authorization notes optional"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                />
            </Modal>

            <Modal
                title={`${bulkStatus === 'Approved' ? 'Bulk Approve' : 'Bulk Reject'} Authorization`}
                open={bulkModalVisible}
                onCancel={() => setBulkModalVisible(false)}
                onOk={handleBulkDelegation}
                okText="Submit"
                destroyOnClose
            >
                <p>
                    <b>{selectedRowKeys.length}</b> item(s) selected
                </p>

                <Input.TextArea
                    rows={3}
                    placeholder="Authorization notes optional"
                    value={bulkReason}
                    onChange={(e) => setBulkReason(e.target.value)}
                />
            </Modal>
        </div>
    );
};

export default AssetDelegationRequest;